import type { AnalyzerWarning } from "@/api/types";
import type { ParsedDxfPreview } from "@/lib/dxfParse";
import { SVG_VIEW_H, SVG_VIEW_W } from "@/lib/dxfTransform";

interface Props {
  warnings: AnalyzerWarning[];
  parsed: ParsedDxfPreview | null;
}

/**
 * Draws warning callouts (rings + leaders + labels) on the analyzer's
 * SVG preview. Anchors each callout to a path bbox parsed from the
 * source SVG (`data-component-id` / `data-loop-id`).
 *
 * Only warnings with a spatial reference render. Today that's:
 *   - tiny_hole, component_nearly_closed, component_self_intersecting,
 *     closed_component_not_polygon — all carry `component_id`.
 * Warnings without coords (e.g. thin_segment) are surfaced in the side
 * WarningsPanel only.
 */
export function DxfWarningOverlay({ warnings, parsed }: Props): JSX.Element | null {
  if (!parsed) return null;

  const items = warnings
    .map((w, i) => {
      const componentId =
        typeof w.params?.component_id === "number"
          ? String(w.params.component_id)
          : undefined;
      if (!componentId) return null;
      const shape =
        parsed.components.get(componentId) ?? parsed.loops.get(componentId);
      if (!shape) return null;
      const isError = w.code === "component_self_intersecting" || w.code === "closed_component_not_polygon";
      return { key: `${w.code}-${i}`, shape, label: shortLabel(w), isError };
    })
    .filter(<T,>(v: T | null): v is T => v !== null);

  if (items.length === 0) return null;

  const STROKE_WARN = "#d97706";
  const STROKE_ERR = "#dc2626";
  const FILL_BG = "#ffffff";

  return (
    <svg
      viewBox={`0 0 ${SVG_VIEW_W} ${SVG_VIEW_H}`}
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
      data-testid="dxf-warning-overlay"
    >
      {items.map((it, idx) => {
        const stroke = it.isError ? STROKE_ERR : STROKE_WARN;
        const r = Math.max(8, Math.min(24, Math.max(it.shape.bbox.w, it.shape.bbox.h) / 2 + 4));
        const cx = it.shape.centroid.x;
        const cy = it.shape.centroid.y;
        // Distribute labels around the part to reduce overlap: alternate
        // sides based on index parity, push outside the SVG centre.
        const labelOnLeft = cx > SVG_VIEW_W / 2;
        const labelX = labelOnLeft ? Math.max(60, cx - 80) : Math.min(SVG_VIEW_W - 60, cx + 80);
        const labelY = idx % 2 === 0 ? Math.max(20, cy - 30) : Math.min(SVG_VIEW_H - 20, cy + 30);
        return (
          <g key={it.key}>
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={stroke}
              strokeWidth="1.4"
              strokeDasharray={it.isError ? undefined : "3 2"}
            />
            <line
              x1={cx + (labelOnLeft ? -r : r)}
              y1={cy}
              x2={labelX + (labelOnLeft ? 4 : -4)}
              y2={labelY}
              stroke={stroke}
              strokeWidth="0.8"
            />
            <rect
              x={labelOnLeft ? labelX - measure(it.label) - 6 : labelX + 4}
              y={labelY - 8}
              width={measure(it.label) + 8}
              height={16}
              rx={3}
              fill={FILL_BG}
              stroke={stroke}
              strokeWidth="0.7"
            />
            <text
              x={labelOnLeft ? labelX - measure(it.label) / 2 - 2 : labelX + measure(it.label) / 2 + 8}
              y={labelY + 3}
              textAnchor="middle"
              fontSize="10"
              fontFamily="ui-sans-serif, system-ui, sans-serif"
              fill={stroke}
            >
              {it.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Compact label: just the warning headline like "⚠ Otvor Ø0,8 mm". */
function shortLabel(w: AnalyzerWarning): string {
  const params = w.params ?? {};
  switch (w.code) {
    case "tiny_hole": {
      const d = numLike(params.diameter_mm);
      return d !== null ? `⚠ Ø ${formatMm(d)} mm` : "⚠ tiny hole";
    }
    case "component_nearly_closed": {
      const g = numLike(params.gap);
      return g !== null ? `⚠ ${formatMm(g)} mm` : "⚠ near-closed";
    }
    case "component_self_intersecting":
      return "⚠ self-cross";
    case "closed_component_not_polygon":
      return "⚠ invalid";
    default:
      return `⚠ ${w.code}`;
  }
}

function numLike(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatMm(v: number): string {
  // Keep two decimals for sub-mm values, else a single decimal.
  return v < 1 ? v.toFixed(2) : v.toFixed(1);
}

/** Crude per-character width estimate for label background sizing. */
function measure(text: string): number {
  return Math.max(20, text.length * 5.5);
}
